import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiTypography from "components/base/UiTypography";
import { ProposalProduct } from "generated/graphql";
import { useFormContext } from "react-hook-form";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import ExpandedWrapper from "./ExpandedWrapper";
import { NUMERIC_SET_VALUE_AS } from "./formUtils";
import StatementOfWork from "./StatementOfWork";
import useMarkupInputProps from "./useMarkupInputProps";

type SubcontractedSectionProps = {
  product: ProposalProduct;
  onMeasureJob?: () => void;
};

export default function SubcontractedSection({
  product,
  onMeasureJob,
}: SubcontractedSectionProps) {
  const { register, setValue } = useFormContext<UpdateProposalProductInputSchema>();
  const markupInputProps = useMarkupInputProps({
    register,
    setValue,
  });

  const fields = [
    {
      label: "Sub Cost",
      value: (+product.subcontractorCost / 100).toFixed(2),
      name: "subcontractorCost",
      numeric: true,
    },
    {
      label: "Sub Name",
      value: product.subcontractorName ?? "",
      name: "subcontractorName",
      numeric: false,
    },
    {
      label: "Markup %",
      value: String(
        product.material.markupPercentageOverride ?? product.material.markupPercentage ?? 0
      ),
      name: "material.markup",
      numeric: true,
      inputProps: markupInputProps,
    },
    {
      label: "Overhead %",
      value: String(
        product.material.overheadPercentageOverride ?? product.material.overheadPercentage ?? 0
      ),
      name: "material.overheadPercentage",
      numeric: true,
    },
    {
      label: "Indirect %",
      value: String(
        product.material.indirectPercentageOverride ?? product.material.indirectPercentage ?? 0
      ),
      name: "material.indirectPercentage",
      numeric: true,
    },
    {
      label: "Sub Proposal ID",
      value: product.subcontractorProposalId ?? "",
      name: "subcontractorProposalId",
      numeric: false,
    },
  ];

  return (
    <ExpandedWrapper>
      <UiCard variant="inner">
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb="8px">
          <UiTypography uiVariant="heading-sm">Profit and Production</UiTypography>
          {onMeasureJob && (
            <UiButton uiVariant="primary-grey" onClick={onMeasureJob}>
              Measure job
            </UiButton>
          )}
        </MDBox>
        <Grid container spacing="8px">
          {fields.map((field) => (
            <Grid item xs={6} md={2.4} key={field.label}>
              <UiFieldGroup
                label={field.label}
                value={field.value}
                textAlign={field.label === "Sub Name" ? "left" : "right"}
                inputProps={
                  (field as { inputProps?: Record<string, unknown> }).inputProps ??
                  register(field.name as any, field.numeric ? NUMERIC_SET_VALUE_AS : {})
                }
              />
            </Grid>
          ))}
        </Grid>
      </UiCard>

      <StatementOfWork />
    </ExpandedWrapper>
  );
}
